import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
  Input,
  Pagination
} from '@nextui-org/react';
import axios from 'axios';
import {
  API_URL,
  TOAST_STYLE_SUCCESS,
  TOAST_STYLE_ERROR,
  SUBSCRIPTION_EXPIRED_CODE,
  SUBSCRIPTION_CANCELED_CODE
} from '../../utils/constants';
import auth from '../../Auth';
import { WithContext as ReactTags } from 'react-tag-input';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import Breadcrumbs from '../Breadcrumbs';
import { Loader2, CircleHelp } from 'lucide-react';
import discordLogo from '../../icons/discord-icon-svgrepo-com.svg';

const KeyCodes = {
  enter: 13,
  space: 32
};

const SEAT_PRICE = 30;

const delimiters = [KeyCodes.enter, KeyCodes.space];

const CounterModal = ({ initialValue = 1, minValue = 1, isOpen, onClose, orgId, orgName, currSeats }) => {
  const [count, setCount] = useState(initialValue);
  const [total, setTotal] = useState(SEAT_PRICE);

  const [referralId, setReferralId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [discountUsed, setDiscountUsed] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReferralStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/referrals?email=${auth.userData.email}`, {
          headers: {
            Authorization: `Bearer ${await auth.getIdToken()}`,
          },
        });
        setDiscountUsed(response.data.discount_used);
      } catch (error) {
        console.error('Error fetching referral status', error);
      }
    };

    if (isOpen) {
      fetchReferralStatus();
    }
  }, [isOpen]);

  const handleIncrement = () => {
    setCount((prev) => prev + 1);
    setTotal((prev) => prev + SEAT_PRICE);
  };
  const handleDecrement = () => {
    if (count > minValue) {
      setCount((prev) => prev - 1);
      setTotal((prev) => prev - SEAT_PRICE);
    }
  };
  const handleSubscribe = async () => {
    setLoading(true);
    if (referralId.length > 0 && referralId.length !== 10) {
      setErrorMessage('Referral ID must be exactly 10 digits.');
      setLoading(false);
      return;
    }
    await axios
      .post(
        `${API_URL}/payments`,
        {
          email: auth.userData.email,
          orgId,
          numSeats: Number(currSeats) + count,
          orgName,
          referral_id: referralId || "",
        },
        {
          headers: {
            Authorization: `Bearer ${await auth.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        }
      )
      .then((res) => {
        const checkout_url = res.data.checkout_url;
        window.location.replace(checkout_url);
        setLoading(false);
        console.log('Checkout page created!');
      })
      .catch((e) => {
        setLoading(false);
        console.error("Couldn't create checkout page!");
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setCount(initialValue);
        setTotal(SEAT_PRICE);
        onClose();
      }}
    >
      <ModalContent>
        <div className='p-6 bg-white rounded-lg shadow-lg'>
          <ModalHeader className='border-b-2 border-gray-200 p-0'>
            <span className='text-2xl mb-5'>Add seats</span>
          </ModalHeader>

          <ModalBody className='pt-4 pb-6'>
            <div className='flex flex-col items-center gap-5'>
              <div className='flex justify-center items-center space-x-4'>
                <Button onPress={handleDecrement} className='bg-red-500 hover:bg-red-600 text-white text-2xl'>
                  -
                </Button>
                <span className='text-lg font-medium'>{count}</span>
                <Button onPress={handleIncrement} className='bg-green-500 hover:bg-green-600 text-white text-2xl'>
                  +
                </Button>
              </div>
              <span>
                You'll have {Number(currSeats) + count} seats in {orgName}
              </span>
              {discountUsed == 'false' && (
                <div className='w-full'>
                  <div className='mt-2 text-center'>Got a referral? Enter code here:</div>
                  <Input
                    className='mt-3'
                    label="Enter 10-digit code"
                    placeholder="Code"
                    value={referralId}
                    onChange={(e) => {
                      const uppercasedValue = e.target.value.toUpperCase();
                      const validValue = uppercasedValue.replace(/[^A-Z0-9]/g, '');

                    
                      if (validValue.length <= 10) {
                        setReferralId(validValue); 
                      }
                    }}
                    maxLength={10}
                  />
                </div>
              )}
              <span className='font-medium text-lg mt-2'>Total cost = ${total}</span>
            </div>
          </ModalBody>
          <ModalFooter className='border-t-2 flex justify-center items-center gap-6 p-0'>
            {!loading ? (
              <Button className='text-base mt-5 ' color='primary' onClick={handleSubscribe}>
                Purchase
              </Button>
            ) : (
              <Loader2 size={40} className='animate-spin mt-5' />
            )}
          </ModalFooter>
        </div>
      </ModalContent>
    </Modal>
  );
};

const ContactModal = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={() => onClose()}>
      <ModalContent>
        <div className='p-6 bg-white rounded-lg shadow-lg'>
          <ModalHeader className='border-b-2 border-gray-200 p-0'>
            <span className='text-2xl mb-5'>Contact us</span>
          </ModalHeader>

          <ModalBody className='pt-4 pb-4 px-2'>
            <p>
              We know we're not perfect, but hey, even Mona Lisa is missing her eyebrows! <br /> If you need us, we're just a Discord
              message away.
            </p>
          </ModalBody>
          <ModalFooter className='border-t-2 p-0 flex justify-center items-center gap-6'>
            <a href='https://discord.gg/h463QYb7Je' target='_blank' rel='noreferrer'>
              <Button fontSize='lg' ghost round auto className='mt-8 flex items-center'>
                <img src={discordLogo} alt='Discord Logo' className='h-6 w-6 mr-2' />
                Ask us anything in the Discord
              </Button>
            </a>
          </ModalFooter>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default function OrganizationsPage() {
  const [organizations, setOrganizations] = useState([]);
  const [emails, setEmails] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [InviteOrgName, setInviteOrgName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [showErrorInvite, setShowErrorInvite] = useState(false);
  const [errorInviteMsg, setErrorInviteMsg] = useState('');
  const [showErrorOrg, setShowErrorOrg] = useState(false);
  const [errorOrgMsg, setErrorOrgMsg] = useState('');
  const [loaderInvite, setLoaderInvite] = useState(false);
  const [loaderOrg, setLoaderOrg] = useState(false);
  const [openSubscription, setOpenSubscription] = useState(false);
  const [seatsOrgId, setSeatsOrgId] = useState('');
  const [seatsOrgName, setSeatsOrgName] = useState('');
  const [currSeats, setCurrSeats] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [inputValue, setInputValue] = useState(''); // Track input value

  const navigate = useNavigate();

  const userData = auth.userData;

  const fetchOrganizations = async () => {
    await axios
      .get(`${API_URL}/organizations`, {
        headers: {
          Authorization: `Bearer ${await auth.getIdToken()}`
        }
      })
      .then((res) => {
        setOrganizations(res.data);
        setFetched(true);
      })
      .catch((e) => {
        setLoaderOrg(false);
        console.error("Couldn't create checkout page!", e);
        toast.error(
          'Unable to retrieve data from the server. Please try again later or contact support if the issue persists.',
          TOAST_STYLE_ERROR
        );
      });
  };

  const handleSendInvitation = async () => {
    const currentEmails = [...emails];
    const newEmail = inputValue.trim();
    let newError = null;

    if (newEmail !== '') {
      if (validateEmail(newEmail)) {
        const emailExists = currentEmails.some((email) => email.id === inputValue);

        if (!emailExists) {
          currentEmails.push({ id: inputValue, text: inputValue });
        }

        setInputValue('');
        newError = false;
      } else {
        setErrorInviteMsg(`Invalid email address: ${newEmail}`);
        setShowErrorInvite(true);
        newError = true;
      }
    } else {
      newError = false;
    }

    if (currentEmails.length !== 0 && newError !== null && !newError) {
      setLoaderInvite(true);

      await axios
        .post(
          `${API_URL}/organizations?sendInvitation`,
          {
            emails: currentEmails,
            org_id: orgId,
            org_name: InviteOrgName,
            sender_email: auth.userData.email
          },
          {
            headers: {
              Authorization: `Bearer ${await auth.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        )
        .then(() => {
          setLoaderInvite(false);
          setModalOpen(false);
          setEmails([]);
          setErrorInviteMsg(false);
          console.log('Success!');
          toast.success('Invitations has been sent', TOAST_STYLE_SUCCESS);
        })
        .catch((e) => {
          setLoaderInvite(false);
          console.log(e.response);
          let errorMsg = '';
          if (e.response.data.error_code === SUBSCRIPTION_EXPIRED_CODE) {
            errorMsg = 'Your seats subscription has expired!';
            toast.error(errorMsg, TOAST_STYLE_ERROR);
          } else if (e.response.data.error_code === SUBSCRIPTION_CANCELED_CODE) {
            errorMsg = 'Your subscription is canceled. Contact us!';
            setShowContact(true);
            toast.error(errorMsg, TOAST_STYLE_ERROR);
          } else {
            errorMsg = 'Error when sending invitations!';
            toast.error(errorMsg, TOAST_STYLE_ERROR);
          }
          setErrorInviteMsg(errorMsg);
          setShowErrorInvite(true);
          console.log('Error!', e);
        });
    } else {
      if (newEmail === '') {
        setErrorInviteMsg("List of emails can't be empty!");
        setShowErrorInvite(true);
      }
    }
  };

  const handleCreateOrganization = async () => {
    setLoaderOrg(true);
    if (orgName !== '') {
      await axios
        .post(
          `${API_URL}/organizations`,
          {
            org_name: orgName
          },
          {
            headers: {
              Authorization: `Bearer ${await auth.getIdToken()}`,
              'Content-Type': 'application/json'
            }
          }
        )
        .then(() => {
          setLoaderOrg(false);
          setNewModalOpen(false);
          toast.success('New organization created!', TOAST_STYLE_SUCCESS);
          fetchOrganizations();
          setOrgName('');
        })
        .catch((err) => {
          setLoaderOrg(false);
          console.log(err);
        });
    } else {
      setShowErrorOrg(true);
      setErrorOrgMsg('Please enter organization name!');
    }
  };

  const handleDelete = (i) => {
    console.log(i);
    setEmails(emails.filter((email, index) => index !== i));
  };

  const handleAddition = (email) => {
    if (validateEmail(email.text)) {
      setEmails([...emails, email]);
      setShowErrorInvite(false);
    } else {
      setErrorInviteMsg(`Invalid email address: ${email.text}`);
      setShowErrorInvite(true);
      setInputValue('');
    }
  };

  const handleInputChange = (input) => {
    setInputValue(input);
    setErrorInviteMsg('');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const crumbs = [
    { path: '/', breadcrumb: 'Home' },
    { path: '/settings', breadcrumb: 'Settings' },
    { path: '/settings/organizations', breadcrumb: 'Organizations' }
  ];

  const [isTooltipVisible, setTooltipVisible] = useState(false);

  const [loadingSwitch, setLoadingSwitch] = useState(false);
  const [currOrgId, setCurrOrgId] = useState(userData.org_id);

  const handleSwitchOrganization = async (org_id) => {
    setLoadingSwitch(true);
    setCurrOrgId(org_id);
    await axios.post(
      `${API_URL}/user_attrs`,
      { org_id },
      {
        headers: {
          Authorization: `Bearer ${auth.syncGetIdToken()}`,
          'Content-Type': 'application/json'
        }
      }
    );
    auth.syncRefreshToken();
  };

  return (
    <div className='flex flex-col w-full p-8 space-y-4'>
      <>
        <Breadcrumbs crumbs={crumbs} />
        {fetched && (
          <div className='flex justify-start items-center'>
            <Button className='mt-4' color='primary' onClick={() => setNewModalOpen(true)}>
              Create organization
            </Button>
          </div>
        )}
        <Modal isOpen={newModalOpen} onClose={() => setNewModalOpen(false)}>
          <ModalContent>
            <ModalHeader>Organization Name</ModalHeader>
            <ModalBody>
              <Input
                value={orgName}
                onChange={(e) => {
                  setOrgName(e.target.value);
                  setShowErrorOrg(false);
                  setErrorOrgMsg('');
                }}
              />
              {showErrorOrg && <span className='text-red-600'>{errorOrgMsg}</span>}
            </ModalBody>
            <ModalFooter>
              {!loaderOrg ? (
                <Button color='primary' onClick={handleCreateOrganization}>
                  Create Organization
                </Button>
              ) : (
                <Loader2 size={35} className='animate-spin' />
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>
        {fetched ? (
          <Table aria-label='My organizations'>
            <TableHeader>
              <TableColumn className='w-1/4' key='org_name'>
                Name
              </TableColumn>
              <TableColumn className='w-1/5' key='user_role'>
                Role
              </TableColumn>
              <TableColumn key='add_member'>Add member</TableColumn>
              <TableColumn key='subscription'>Add seats</TableColumn>
              <TableColumn key='assign'>Set organization</TableColumn>
            </TableHeader>
            <TableBody>
              {organizations.map((item, index) => (
                <TableRow>
                  <TableCell>
                    <Link
                      to={`/settings/organizations/invitations/${item.org_id}`}
                      className='text-blue-500 hover:text-blue-700 font-semibold'
                    >
                      {item.organization_name}
                    </Link>
                  </TableCell>
                  <TableCell>{item.role}</TableCell>
                  <TableCell>
                    {item.role === 'owner' && item.type === 'PUBLIC' && (
                      <Button
                        color='primary'
                        onClick={() => {
                          if (item.num_members != item.num_seats) {
                            setInviteOrgName(item.organization_name);
                            setOrgId(item.org_id);
                            setModalOpen(true);
                          } else {
                            setCurrSeats(item.num_seats);
                            setSeatsOrgId(item.org_id);
                            setSeatsOrgName(item.organization_name);
                            setOpenSubscription(true);
                          }
                        }}
                      >
                        {item.num_members == item.num_seats ? 'Full' : 'Invite'} {item.num_members}/{item.num_seats}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.role === 'owner' && item.type === 'PUBLIC' && (
                      <Button
                        color='primary'
                        onClick={() => {
                          setOpenSubscription(true);
                          setSeatsOrgId(item.org_id);
                          setCurrSeats(item.num_seats);
                          setSeatsOrgName(item.organization_name);
                        }}
                      >
                        Add more seats
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    {currOrgId !== item.org_id && (
                      <Button
                        color='primary'
                        onClick={() => {
                          handleSwitchOrganization(item.org_id);
                        }}
                      >
                        Set as current
                      </Button>
                    )}
                    {!loadingSwitch && currOrgId === item.org_id && 'Current'}
                    {loadingSwitch && currOrgId === item.org_id && <Loader2 className='animate-spin' />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Loader2 size={48} className='m-auto animate-spin' />
        )}
        <Modal
          isOpen={modalOpen}
          onClose={() => {
            setEmails([]);
            setModalOpen(false);
            setErrorInviteMsg('');
            setShowErrorInvite(false);
            setLoaderInvite(false);
          }}
        >
          <ModalContent>
            <ModalHeader className='mb-2'>Invite members</ModalHeader>
            <ModalBody className='gap-2'>
              <div className='relative inline-block'>
                <span className='flex gap-1 items-center'>
                  Add emails{' '}
                  <CircleHelp
                    size={20}
                    onMouseEnter={() => setTooltipVisible(true)}
                    onMouseLeave={() => setTooltipVisible(false)}
                    className='cursor-pointer '
                  />
                </span>

                {isTooltipVisible && (
                  <div className='absolute left-28 bottom-0 bg-gray-500 text-white text-sm rounded-lg p-2 shadow-lg'>
                    Press <b>Enter or Space</b> to add email to the list
                  </div>
                )}
              </div>
              <ReactTags
                tags={emails}
                handleDelete={handleDelete}
                handleAddition={handleAddition}
                delimiters={delimiters}
                inputFieldPosition='bottom'
                handleInputChange={handleInputChange}
                allowDragDrop={false}
                placeholder='Enter email and press Enter or Space'
                handleDrag={() => { }}
                classNames={{
                  tags: 'tagsContainer',
                  tagInput: 'tagInputContainer',
                  tagInputField: 'tagInputField h-8',
                  selected: 'selectedTags',
                  tag: 'tagItem',
                  remove: 'removeTag'
                }}
              />
              {showErrorInvite && <span className='text-red-600'>{errorInviteMsg}</span>}
            </ModalBody>

            <ModalFooter>
              {!loaderInvite ? (
                <Button color='primary' onClick={handleSendInvitation}>
                  Invite
                </Button>
              ) : (
                <Loader2 size={35} className='animate-spin' />
              )}
            </ModalFooter>
          </ModalContent>
        </Modal>

        <CounterModal
          isOpen={openSubscription}
          onClose={() => setOpenSubscription(false)}
          orgId={seatsOrgId}
          currSeats={currSeats}
          orgName={seatsOrgName}
        />
      </>
      <ContactModal isOpen={showContact} onClose={() => setShowContact(false)} />
    </div>
  );
}
